// UI registry for the Documents section. Keys mirror DOCUMENT_TYPES in
// backend/src/services/clinicalDocumentService.js; the server is the
// authority on which inputs each type accepts, this file only drives the form.
//
// `button` is the short text on the type picker, `tip` the full name shown
// in the button's tooltip; `label` is the full name used for the form heading,
// the review title and the file name.

export const DOCUMENT_TYPES = [
  {
    key: "referral",
    label: "Referral letter",
    button: "Referral",
    tip: "Referral Letter",
    icon: "fa-solid fa-paper-plane",
    defaultRange: "this_note",
    hasRecipient: true,
    help: "Refer the patient to another professional. Starts from today's note.",
  },
  {
    key: "referrer_update",
    label: "Letter to referring doctor",
    button: "Letter",
    tip: "Letter to Referring Doctor",
    icon: "fa-solid fa-user-doctor",
    defaultRange: "all",
    hasRecipient: true,
    help: "A progress update to the doctor who referred the patient.",
  },
  {
    key: "progress_report",
    label: "Progress report",
    button: "Progress",
    tip: "Progress Report",
    icon: "fa-solid fa-file-lines",
    defaultRange: "all",
    hasRecipient: false,
    help: "A report for a medical aid, employer or another professional.",
  },
  {
    key: "sick_note",
    label: "Sick note",
    button: "Sick",
    tip: "Sick Note",
    icon: "fa-solid fa-file-medical",
    defaultRange: "this_note",
    hasRecipient: false,
    form: true,
    help: "A medical certificate in fixed wording from the dates you enter. Needs your registration number on your profile.",
  },
  {
    key: "return_to_work",
    label: "Return to work",
    button: "Return",
    tip: "Return to Work",
    icon: "fa-solid fa-briefcase-medical",
    defaultRange: "this_note",
    hasRecipient: false,
    form: true,
    help: "Certifies the date the patient may return to work, with any restrictions.",
  },
  {
    key: "motivation",
    label: "Medical aid motivation",
    button: "MA Motivation",
    tip: "Medical Aid Motivation",
    icon: "fa-solid fa-file-invoice-dollar",
    defaultRange: "all",
    hasRecipient: false,
    // The scheme is filled from the patient record; the review screen still
    // lets the practitioner correct it before sending.
    editableRecipient: true,
    help: "Motivates for approval or funding. Addressed to the patient's scheme using the details on their record.",
  },
  {
    key: "imaging_request",
    label: "Imaging request",
    button: "Imaging",
    tip: "Imaging",
    icon: "fa-solid fa-x-ray",
    defaultRange: "this_note",
    hasRecipient: true,
    help: "A request to a radiology practice with the clinical question to answer.",
  },
];

export const FITNESS_OPTIONS = [
  { key: "unfit", label: "Unfit for work" },
  { key: "light_duty", label: "Fit for light duty" },
];

export const MODALITY_OPTIONS = ["X-ray", "Ultrasound", "MRI", "CT", "Other"];

// Local calendar date. toISOString() is UTC, which is a day behind after
// 22:00 South African time.
export const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// The server also accepts three_months and twelve_months; the picker offers
// only the last note, everything, or a custom window.
export const RANGE_OPTIONS = [
  { key: "this_note", label: "Last note" },
  { key: "all", label: "All history" },
  { key: "custom", label: "Custom" },
];

export const LANGUAGE_OPTIONS = [
  { key: "en", label: "English" },
  { key: "af", label: "Afrikaans" },
];

// Mirrors DEFAULT_REPORT_HEADINGS on the server. Used when the practice has
// no Report Export template, and as the starting chips either way.
export const DEFAULT_REPORT_HEADINGS = [
  "Background",
  "Assessment findings",
  "Treatment to date",
  "Progress",
  "Recommendations",
];

export const typeByKey = (key) => DOCUMENT_TYPES.find((t) => t.key === key) || DOCUMENT_TYPES[0];

export const formatDateGB = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? String(iso)
    : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};
